import { useSelection } from "@/contexts/SelectionContext";
import { useUnlinkLesson } from "@/lib/hooks/lessons/useUnlinkLesson";
import { useUnlinkProject } from "@/lib/hooks/projects/useUnlinkProject";
import { useUnlinkSlide } from "@/lib/hooks/slides/useUnlinkSlide";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { loadSlide } from "@/store/slideEditorSlice";
import { Button, Modal } from "@ai-teach/ui-web";
import { useState } from "react";

interface DeleteModalProps {
  isOpen: boolean;
}

export function UnlinkModal({ isOpen }: DeleteModalProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    selectedProjectIds,
    selectedLessonIds,
    clearAllSelections,
    toggleUnlinkModalOpen,
    isUnlinkModalOpen,
  } = useSelection();

  const { unlinkSlide, loading: unlinkSlideLoading } = useUnlinkSlide();
  const { unlinkProject, loading: unlinkProjectLoading } = useUnlinkProject();
  const { unlinkLesson, loading: unlinkLessonLoading } = useUnlinkLesson();

  const dispatch = useAppDispatch();
  const slide = useAppSelector((state) => state.slideEditor.present.slide);

  const isLoading = unlinkSlideLoading || unlinkProjectLoading || unlinkLessonLoading;

  const handleClose = () => {
    const totalItems = selectedLessonIds.length + selectedProjectIds.length;
    if (totalItems === 1) {
      clearAllSelections();
    }
    setErrorMessage(null);
    toggleUnlinkModalOpen(false);
  };

  const handleUnlink = async () => {
    if (!isUnlinkModalOpen) return;

    const { itemId, type, originalItemId = null } = isUnlinkModalOpen;

    if (type === "Slide") {
      await unlinkSlide(itemId, originalItemId);
      if (slide) {
        dispatch(loadSlide({ ...slide, isLinkedCopy: false }));
      }
    } else if (type === "Lesson") {
      await unlinkLesson(itemId, originalItemId);
    } else if (type === "Project") {
      await unlinkProject(itemId, originalItemId);
    }

    toggleUnlinkModalOpen(false);
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={errorMessage ? "Error!" : "Warning!"}
      size="sm"
      style="danger"
    >
      <div className="flex flex-col">
        {errorMessage ? (
          <>
            <p className="text-secondary-200 mb-6 text-center">{errorMessage}</p>
            <div className="flex justify-center">
              <Button onClick={handleClose} variant="secondary">
                Close
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-secondary-200 mb-6 text-center">
              This item is a linked copy. You can edit it after unlinking.
            </p>

            <div className="flex gap-3 justify-between">
              <Button onClick={handleClose} variant="secondary">
                Cancel
              </Button>
              <Button onClick={handleUnlink} variant="danger" disabled={isLoading}>
                Unlink
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
